// src/ConsumerApp/Screens/ProductDetails/ProductDetailsScreen.js

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../../DesignSystem/Navigation/Navbar';
import CenteredContainer from '../../DesignSystem/Layout/CenteredContainer';
import ProductOptionsModal from './components/ProductOptionsModal';
import Bucket from '../Bucket/Bucket';
import { guestBucketManager } from '../../utils/guestBucketManager';
import { useToast } from '../../context/ToastContext';
import { useAuthModal } from '../../context/AuthModalContext';
import { emitCartItemAdded, emitCartItemUpdated, emitCartItemRemoved } from '../../utils/cartEvents';

// Real SVGs from assets/icons
import SealCheckRed from '../../../assets/icons/SealCheckRed.svg';
import FileText from '../../../assets/icons/FileText.svg';
import TruckRed from '../../../assets/icons/TruckRed.svg';
import WhitePlus from '../../../assets/icons/WhitePlus.svg';
import WhiteMinus from '../../../assets/icons/WhiteMinus.svg';
import WhatsappLogo from '../../../assets/icons/WhatsappLogo.svg';
import BookmarkIcon from '../../../assets/icons/bookmark.svg';
import FillBookmarkIcon from '../../../assets/icons/FillBookmark.svg';
import ToteIcon from '../../../assets/icons/tote.svg';
import bucketConcrete from '../../../assets/icons/bucketConcrete.svg';

// Custom Hooks
import {
  useGetProductById,
  useGetFaqsBySubCategoryId,
  useGetBucketSummary,
  useAddItemToBucket,
  useUpdateBucketItemQuantity,
  useDeleteBucketItem,
  useGetRelatedProducts,
  useGetProductVariants,
} from '../../api/products/product.hooks';
import {
  useGetSavedList,
  useAddItemToSavedList,
  useRemoveItemFromSavedList,
} from '../../api/savedList/savedList.hooks';

const IMAGE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';
const BUSINESS_WHATSAPP_NUMBER = '918639065454';

export default function ProductDetailsScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { openLoginModal } = useAuthModal();

  const queryParams = new URLSearchParams(location.search);
  const routeProductId =
    location.state?.productId ||
    location.state?.item?.product_id ||
    location.state?.item?._id ||
    queryParams.get('id');

  const [activeProductId, setActiveProductId] = useState(routeProductId);
  const [isBucketOpen, setIsBucketOpen] = useState(false);

  // 1. Reactive Customer Session
  const [customer, setCustomer] = useState(() => {
    try {
      const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    const syncAuth = () => {
      try {
        const stored = sessionStorage.getItem('userData') || localStorage.getItem('userData');
        setCustomer(stored ? JSON.parse(stored) : null);
      } catch {
        setCustomer(null);
      }
    };

    window.addEventListener('auth-state-change', syncAuth);
    window.addEventListener('storage', syncAuth);
    return () => {
      window.removeEventListener('auth-state-change', syncAuth);
      window.removeEventListener('storage', syncAuth);
    };
  }, []);

  const customerId = customer?._id || customer?.id;
  const hubId = customer?.hubId || '';

  // 2. Guest Bucket Listener
  const [guestBucketMap, setGuestBucketMap] = useState(() => guestBucketManager.getQuantityMap());
  const [guestCount, setGuestCount] = useState(() => guestBucketManager.getTotalCount());

  useEffect(() => {
    const handleGuestChange = () => {
      setGuestBucketMap(guestBucketManager.getQuantityMap());
      setGuestCount(guestBucketManager.getTotalCount());
    };

    window.addEventListener('guest-bucket-change', handleGuestChange);
    return () => window.removeEventListener('guest-bucket-change', handleGuestChange);
  }, []);

  // 3. API Queries
  const { data: productRes, isLoading: isProductLoading } = useGetProductById(activeProductId);
  const productData = productRes?.productData || productRes;
  const subCategoryId = productData?.product_sub_category_id;

  const { data: faqs = [] } = useGetFaqsBySubCategoryId(subCategoryId);
  const { data: relatedProducts = [] } = useGetRelatedProducts({
    customer_id: customerId,
    product_id: activeProductId,
  });

  const { data: bucketSummary } = useGetBucketSummary(customerId);
  const { data: savedList = [] } = useGetSavedList(customerId);

  // Mutations
  const addItemMutation = useAddItemToBucket();
  const updateItemMutation = useUpdateBucketItemQuantity();
  const deleteItemMutation = useDeleteBucketItem();
  const addSavedItemMutation = useAddItemToSavedList();
  const removeSavedItemMutation = useRemoveItemFromSavedList();
  const { mutateAsync: fetchProductVariants } = useGetProductVariants();

  // Total cart count for the floating badge
  const totalCartCount = customerId
    ? bucketSummary?.buy_now_bucket?.items_count || 0
    : guestCount;

  // 4. View State
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [mainImgError, setMainImgError] = useState(false);
  const [thumbErrors, setThumbErrors] = useState({});

  // Options Sheet State
  const [isOptionsSheetVisible, setIsOptionsSheetVisible] = useState(false);
  const [variantsList, setVariantsList] = useState([]);
  const [isLoadingVariants, setIsLoadingVariants] = useState(false);
 
  // Brick Points State
  const [isPointsModalOpen, setIsPointsModalOpen] = useState(false);
  const isPointsEligible = productRes?.is_points_eligible === true;
  const productPoints = Number(productRes?.points || 0);

  // Out of Stock Detection
  const isOutOfStock = Boolean(
    productData?.out_of_stock === true ||
    productData?.is_out_of_stock === true ||
    productRes?.out_of_stock === true ||
    productRes?.is_out_of_stock === true ||
    productData?.inventory_status?.toLowerCase() === 'out_of_stock' ||
    productData?.stock === 0
  );

  const isSaved = useMemo(() => {
    return savedList.some(
      (item) => String(item.product_id || item.item_id || item._id) === String(activeProductId)
    );
  }, [savedList, activeProductId]);

  const bucketItemsMap = useMemo(() => {
    const map = new Map();
    const buyNowItems = bucketSummary?.buy_now_bucket?.items || [];
    buyNowItems.forEach((bItem) => {
      if (bItem?.item_id) {
        map.set(String(bItem.item_id), {
          quantity: Number(bItem.quantity) || 1,
          bucketId: bucketSummary?.buy_now_bucket?.bucket_id || bucketSummary?.buy_now_bucket?._id,
        });
      }
    });
    return map;
  }, [bucketSummary]);

  const inCartQty = customerId
    ? bucketItemsMap.get(String(activeProductId))?.quantity || 0
    : guestBucketMap.get(String(activeProductId)) || 0;

  const name = productData?.product_name || 'Construction Material';
  const unitText = productData?.uom || productData?.UOM || 'Unit';
  const buyNowPrice = Number(productData?.buy_now?.unit_price || productData?.selling_price || 0);
  const mrpPrice = Number(productData?.mrp || 0);
  const discountText = productData?.discount ? `${productData.discount}% off` : '';

  const hasBuyNow = Boolean(
    productData?.purchase_mode?.toLowerCase()?.includes('buy now') ||
    productData?.buy_now?.is_active
  );
  const hasQuote = Boolean(
    productData?.purchase_mode?.toLowerCase()?.includes('quote') ||
    productData?.rfq?.is_active
  );
  const isOnlyQuote = !hasBuyNow && hasQuote;

  const optionsCount = Number(productRes?.other_options_count || productData?.other_options_count || 0);
  const hasOptions = optionsCount > 0;

  const productImages = useMemo(() => {
    if (productData?.images && productData.images.length > 0) {
      return productData.images.map((img) =>
        img.startsWith('http') ? img : `${IMAGE_URL}${img}`
      );
    }
    const singleImg = productData?.product_image_url || productData?.product_image;
    if (singleImg) {
      return [singleImg.startsWith('http') ? singleImg : `${IMAGE_URL}${singleImg}`];
    }
    return [];
  }, [productData]);

  const handleToggleSave = (e) => {
    e.stopPropagation();
    if (!customerId) {
      showToast('Please log in to save the item.', 'info');
      if (openLoginModal) openLoginModal();
      return;
    }
    if (isSaved) {
      removeSavedItemMutation.mutate({ customer_id: customerId, product_id: activeProductId });
    } else {
      addSavedItemMutation.mutate({ customer_id: customerId, product_id: activeProductId });
    }
  };

  const handleOpenOptions = async () => {
    setIsLoadingVariants(true);
    try {
      const purchase_mode = isOnlyQuote ? 'Quote' : 'Buy Now';
      const response = await fetchProductVariants({
        product_id: activeProductId,
        purchase_mode: purchase_mode,
      });
      const list = Array.isArray(response) ? response : response?.product_variants || [];
      setVariantsList(list);
      setIsOptionsSheetVisible(true);
    } catch (err) {
      console.error('Error fetching product variants:', err);
    } finally {
      setIsLoadingVariants(false);
    }
  };

  const handleAddOption = (selectedOption) => {
    const selectedId = String(selectedOption.product_id || selectedOption._id || selectedOption.id);
    if (activeProductId !== selectedId) {
      setActiveProductId(selectedId);
    }
  };

  const handleInitialAdd = () => {
    if (isOutOfStock) return;

    if (hasOptions) {
      handleOpenOptions();
      return;
    }

    const minQty = productData?.buy_now?.min_qty || 1;

    if (!customerId) {
      guestBucketManager.addItem(productData, minQty);
    } else {
      addItemMutation.mutate({
        customer_id: customerId,
        hubId,
        item_id: activeProductId,
        purchase_type: isOnlyQuote ? 'RFQ' : 'BUY_NOW',
        quantity: minQty,
      });
    }

    emitCartItemAdded(productData, minQty, activeProductId);
  };

  const handleUpdateQuantity = (newQty) => {
    const minQty = productData?.buy_now?.min_qty || 1;

    if (!customerId) {
      if (newQty < minQty) {
        guestBucketManager.removeItem(activeProductId);
      } else {
        guestBucketManager.updateQuantity(activeProductId, newQty);
      }
    } else {
      if (newQty < minQty) {
        deleteItemMutation.mutate({
          customer_id: customerId,
          item_id: activeProductId,
          bucket_id: bucketItemsMap.get(String(activeProductId))?.bucketId,
        });
      } else {
        updateItemMutation.mutate({
          customer_id: customerId,
          hubId,
          item_id: activeProductId,
          purchase_type: isOnlyQuote ? 'RFQ' : 'BUY_NOW',
          quantity: newQty,
        });
      }
    }

    if (newQty < minQty) {
      emitCartItemRemoved(activeProductId);
    } else {
      emitCartItemUpdated(productData, newQty, activeProductId);
    }
  };

  const handleWhatsAppQuote = () => {
    const message = encodeURIComponent(
      `Hi, I would like to request price quotation for: ${name} (Product ID: ${activeProductId})`
    );
    window.open(`https://wa.me/${BUSINESS_WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const parsedSpecifications = useMemo(() => {
    const specs = productData?.specifications;
    if (!specs) return [];
    try {
      if (Array.isArray(specs)) {
        return specs.filter((item) => item && typeof item === 'object' && !Array.isArray(item));
      }
      if (typeof specs === 'string') {
        const parsed = JSON.parse(specs.trim());
        return Array.isArray(parsed) ? parsed : [];
      }
      if (typeof specs === 'object') return [specs];
      return [];
    } catch {
      return [];
    }
  }, [productData?.specifications]);

  if (isProductLoading) {
    return (
      <div style={styles.pageShell}>
        <Navbar />
        <div style={styles.loadingBox}>
          <span>Loading product details...</span>
        </div>
      </div>
    );
  }

  const currentMainImg = productImages[selectedImageIndex] || productImages[0];

  return (
    <div style={styles.pageShell}>
      <style>{`
        /* ── Hero Grid ── */
        .bb-pdp-hero-grid {
          display: grid;
          grid-template-columns: 460px 1fr;
          gap: 40px;
          align-items: flex-start;
          width: 100%;
        }

        .bb-pdp-gallery-col {
          display: flex;
          flex-direction: column;
          gap: 16px;
          position: sticky;
          top: 96px;
          width: 100%;
        }

        .bb-pdp-main-img-box {
          width: 100%;
          height: 420px;
          background-color: #FFFFFF;
          border-radius: 16px;
          border: 1px solid #CBD5E1;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          position: relative;
          overflow: hidden;
          box-sizing: border-box;
        }

        .bb-pdp-thumbs-row {
          display: flex;
          gap: 12px;
          overflow-x: auto;
          padding-bottom: 4px;
          scrollbar-width: none;
        }
        .bb-pdp-thumbs-row::-webkit-scrollbar { display: none; }

        .bb-pdp-actions-desktop {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-top: 4px;
        }

        .bb-pdp-mobile-bottom-bar {
          display: none;
        }

        .bb-pdp-floating-bucket-btn {
          display: none;
        }

        /* ── TABLET & MOBILE RESPONSIVE ADAPTATIONS (<= 992px) ── */
        @media (max-width: 992px) {
          .bb-pdp-hero-grid {
            grid-template-columns: 1fr;
            gap: 24px;
          }

          .bb-pdp-gallery-col {
            position: static;
          }

          .bb-pdp-main-img-box {
            height: 340px;
            padding: 16px;
          }
        }

        /* ── SMALL MOBILE PHONES (<= 768px) ── */
        @media (max-width: 768px) {
          .bb-pdp-main-img-box {
            height: 280px;
            border-radius: 14px;
          }

          .bb-pdp-actions-desktop {
            display: none;
          }

          /* Floating Bucket Button: Anchored above the mobile bottom bar on the right side */
          .bb-pdp-floating-bucket-btn {
            display: flex;
            position: fixed;
            bottom: calc(72px + env(safe-area-inset-bottom, 0px));
            right: 16px;
            width: 50px;
            height: 50px;
            border-radius: 25px;
            background-color: #FFFFFF;
            border: 1.5px solid #CBD5E1;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
            align-items: center;
            justify-content: center;
            z-index: 9999;
            cursor: pointer;
            transition: transform 0.15s ease;
            box-sizing: border-box;
          }

          .bb-pdp-floating-bucket-btn:active {
            transform: scale(0.92);
          }

          .bb-pdp-floating-badge {
            position: absolute;
            top: -4px;
            right: -4px;
            background-color: #E9001E;
            color: #FFFFFF;
            font-size: 11px;
            font-weight: 900;
            border-radius: 999px;
            min-width: 18px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0 4px;
            border: 2px solid #FFFFFF;
            box-shadow: 0 2px 4px rgba(233, 0, 30, 0.35);
          }

          /* Fixed Action Bar for Thumb Interaction */
          .bb-pdp-mobile-bottom-bar {
            display: flex;
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            background-color: #FFFFFF;
            border-top: 1px solid #E2E8F0;
            padding: 10px 16px;
            padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px));
            box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
            z-index: 10000;
            align-items: center;
            justify-content: space-between;
            gap: 12px;
            box-sizing: border-box;
          }

          .bb-pdp-content-stack {
            padding-bottom: calc(90px + env(safe-area-inset-bottom, 0px)) !important;
          }

          .bb-pdp-specs-row {
            flex-direction: column !important;
            gap: 4px !important;
          }

          .bb-pdp-specs-label {
            width: 100% !important;
            font-size: 12px;
          }

          .bb-pdp-specs-val {
            width: 100% !important;
            font-size: 13px;
          }
        }
      `}</style>

      <Navbar />

      <CenteredContainer>
        <div style={styles.contentStack} className="bb-pdp-content-stack">
          {/* Dual-Column Product Hero */}
          <div className="bb-pdp-hero-grid">
            <div className="bb-pdp-gallery-col">
              <div className="bb-pdp-main-img-box">
                {currentMainImg && !mainImgError ? (
                  <img
                    src={currentMainImg}
                    alt={name}
                    style={{
                      ...styles.mainImg,
                      filter: isOutOfStock ? 'grayscale(80%)' : 'none',
                      opacity: isOutOfStock ? 0.7 : 1,
                    }}
                    onError={() => setMainImgError(true)}
                  />
                ) : (
                  <div style={styles.toteFallbackWrapper}>
                    <img src={ToteIcon} alt="placeholder" style={styles.toteFallbackIcon} />
                  </div>
                )}

                <button
                  type="button"
                  style={styles.bookmarkFloatBtn}
                  onClick={handleToggleSave}
                  title={isSaved ? 'Remove from Shortlist' : 'Save for Later'}
                >
                  <img
                    src={isSaved ? FillBookmarkIcon : BookmarkIcon}
                    alt="bookmark"
                    style={{
                      width: 18,
                      height: 18,
                      filter: isSaved
                        ? 'invert(16%) sepia(85%) saturate(6120%) hue-rotate(348deg) brightness(91%) contrast(106%)'
                        : 'none',
                    }}
                  />
                </button>
              </div>

              {productImages.length > 1 && (
                <div className="bb-pdp-thumbs-row">
                  {productImages.map((imgUrl, idx) => {
                    const isSelected = selectedImageIndex === idx;
                    const hasThumbError = thumbErrors[idx];

                    return (
                      <div
                        key={idx}
                        style={{
                          ...styles.thumbWrapper,
                          borderColor: isSelected ? '#C7061F' : '#E2E8F0',
                          boxShadow: isSelected ? '0 0 0 1px #C7061F' : 'none',
                          opacity: isOutOfStock ? 0.7 : 1,
                        }}
                        onClick={() => {
                          setSelectedImageIndex(idx);
                          setMainImgError(false);
                        }}
                      >
                        {imgUrl && !hasThumbError ? (
                          <img
                            src={imgUrl}
                            alt={`thumb-${idx}`}
                            style={styles.thumbImg}
                            onError={() => setThumbErrors((prev) => ({ ...prev, [idx]: true }))}
                          />
                        ) : (
                          <img
                            src={ToteIcon}
                            alt="fallback"
                            style={{ width: 22, height: 22, opacity: 0.35 }}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={styles.detailsColumn}>
              {/* Zepto-Style Out Of Stock Notice Box */}
              {isOutOfStock && (
                <div style={styles.zeptoOutOfStockBox}>
                  <strong style={styles.zeptoOutOfStockTitle}>Current item is out of stock</strong>
                  <span style={styles.zeptoOutOfStockSub}>Please try changing the item or check alternative options</span>
                </div>
              )}

              <div style={styles.headerCluster}>
                <span style={styles.categoryBadge}>
                  {productData?.product_category_name || 'Building Material'}
                </span>
                <h1 style={styles.productTitleText}>{name}</h1>
              </div>

              <div style={styles.priceContainer}>
                <div style={styles.priceRow}>
                  <div style={styles.priceWithUnitGroup}>
                    <span style={{
                      ...styles.sellingPriceText,
                      color: isOutOfStock ? '#64748B' : '#0F172A',
                    }}>
                      ₹{buyNowPrice.toLocaleString('en-IN')}
                    </span>
                    {unitText && <span style={styles.unitSuffixText}>/ {unitText}</span>}
                  </div>

                  {!isOutOfStock && mrpPrice > buyNowPrice && (
                    <span style={styles.mrpStrikeText}>
                      ₹{mrpPrice.toLocaleString('en-IN')}
                    </span>
                  )}

                  {!isOutOfStock && discountText && (
                    <span style={styles.discountPill}>{discountText}</span>
                  )}
                </div>
                <span style={styles.taxInclusiveText}>Inclusive of GST</span>
              </div>

              {/* Brick Points Banner */}
              {isPointsEligible && (
                <div style={styles.pointsStrip} onClick={() => setIsPointsModalOpen(true)}>
                  <div style={styles.pointsStripLeft}>
                    <span style={{ fontSize: 16 }}>🪙</span>
                    <span style={styles.pointsStripText}>
                      Earn <strong style={{ color: '#D97706' }}>{productPoints} Brick Points</strong> on this item
                    </span>
                  </div>
                  <span style={styles.pointsStripLink}>How it works</span>
                </div>
              )}

              {/* Desktop Actions */}
              <div className="bb-pdp-actions-desktop">
                {isOutOfStock ? (
                  <div style={styles.outOfStockPillDesktop}>
                    OUT OF STOCK
                  </div>
                ) : inCartQty > 0 ? (
                  <div style={styles.qtyStepper}>
                    <button
                      type="button"
                      style={styles.stepperBtn}
                      onClick={() => handleUpdateQuantity(inCartQty - 1)}
                      aria-label="Decrease quantity"
                    >
                      <img src={WhiteMinus} alt="-" style={{ width: 16, height: 16 }} />
                    </button>
                    <span style={styles.stepperCount}>{inCartQty}</span>
                    <button
                      type="button"
                      style={styles.stepperBtn}
                      onClick={() => handleUpdateQuantity(inCartQty + 1)}
                      aria-label="Increase quantity"
                    >
                      <img src={WhitePlus} alt="+" style={{ width: 16, height: 16 }} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    style={{
                      ...styles.zeptoAddBtn,
                      backgroundColor: isOnlyQuote ? '#1E293B' : '#FFFFFF',
                      borderColor: isOnlyQuote ? '#1E293B' : '#E9001E',
                    }}
                    onClick={handleInitialAdd}
                    disabled={isLoadingVariants}
                  >
                    <span
                      style={{
                        ...styles.zeptoAddBtnText,
                        color: isOnlyQuote ? '#FFFFFF' : '#E9001E',
                      }}
                    >
                      {isOnlyQuote ? 'ADD TO QUOTE' : 'ADD TO BUCKET'}
                    </span>
                    {hasOptions && (
                      <span
                        style={{
                          ...styles.optionsBadgeText,
                          color: isOnlyQuote ? '#94A3B8' : '#707B8C',
                        }}
                      >
                        {optionsCount} Options
                      </span>
                    )}
                  </button>
                )}

                <button
                  type="button"
                  style={styles.whatsAppQuoteBtn}
                  onClick={handleWhatsAppQuote}
                >
                  <img src={WhatsappLogo} alt="whatsapp" style={{ width: 16, height: 16 }} />
                  <span>Custom Quote</span>
                </button>
              </div>

              <div style={styles.trustBadgesGrid}>
                <div style={styles.trustBadgeItem}>
                  <img src={SealCheckRed} alt="seal" style={{ width: 16, height: 16 }} />
                  <span>Verified Grade</span>
                </div>
                <div style={styles.trustBadgeItem}>
                  <img src={FileText} alt="gst" style={{ width: 16, height: 16 }} />
                  <span>GST Tax Invoice</span>
                </div>
                <div style={styles.trustBadgeItem}>
                  <img src={TruckRed} alt="truck" style={{ width: 16, height: 16 }} />
                  <span>Direct Site Dispatch</span>
                </div>
              </div>

              <div style={styles.specsCard}>
                <h3 style={styles.specsTitle}>Specifications</h3>
                {parsedSpecifications.length > 0 ? (
                  <div style={styles.specsTable}>
                    {parsedSpecifications.map((spec, idx) => {
                      const [label, value] = Object.entries(spec)[0] || ['N/A', 'N/A'];
                      return (
                        <div key={idx} style={styles.specsTableRow} className="bb-pdp-specs-row">
                          <span style={styles.specsLabel} className="bb-pdp-specs-label">{label}</span>
                          <span style={styles.specsValue} className="bb-pdp-specs-val">{String(value)}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span style={styles.noSpecsText}>Standard industrial specifications apply.</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </CenteredContainer>

      {/* Floating Bucket Button: Appears when items are in bucket, sitting right above the mobile footer */}
      {totalCartCount > 0 && (
        <button
          type="button"
          className="bb-pdp-floating-bucket-btn"
          onClick={() => setIsBucketOpen(true)}
          aria-label="View bucket"
        >
          <img src={bucketConcrete} alt="bucket" style={{ width: 24, height: 24 }} />
          <span className="bb-pdp-floating-badge">{totalCartCount}</span>
        </button>
      )}

      {/* Sticky Bottom Action Bar for Mobile Viewports (<= 768px) */}
      <div className="bb-pdp-mobile-bottom-bar">
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{ fontSize: 11, color: '#64748B', fontWeight: 600 }}>Total Price</span>
          <span style={{
            fontSize: 18,
            fontWeight: 900,
            color: isOutOfStock ? '#64748B' : '#0F172A',
            lineHeight: '22px'
          }}>
            ₹{buyNowPrice.toLocaleString('en-IN')}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {isOutOfStock ? (
            <div style={styles.outOfStockPillMobile}>
              OUT OF STOCK
            </div>
          ) : inCartQty > 0 ? (
            <div style={{ ...styles.qtyStepper, width: 130, height: 42 }}>
              <button
                type="button"
                style={styles.stepperBtn}
                onClick={() => handleUpdateQuantity(inCartQty - 1)}
              >
                <img src={WhiteMinus} alt="-" style={{ width: 15, height: 15 }} />
              </button>
              <span style={styles.stepperCount}>{inCartQty}</span>
              <button
                type="button"
                style={styles.stepperBtn}
                onClick={() => handleUpdateQuantity(inCartQty + 1)}
              >
                <img src={WhitePlus} alt="+" style={{ width: 15, height: 15 }} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              style={{
                ...styles.zeptoAddBtn,
                height: 42,
                minWidth: 140,
                backgroundColor: isOnlyQuote ? '#0F172A' : '#E9001E',
                borderColor: isOnlyQuote ? '#0F172A' : '#E9001E',
              }}
              onClick={handleInitialAdd}
              disabled={isLoadingVariants}
            >
              <span style={{ ...styles.zeptoAddBtnText, color: '#FFFFFF' }}>
                {isOnlyQuote ? 'ADD TO QUOTE' : 'ADD TO BUCKET'}
              </span>
              {hasOptions && (
                <span style={{ ...styles.optionsBadgeText, color: '#F1F5F9' }}>
                  {optionsCount} Options
                </span>
              )}
            </button>
          )}

          <button
            type="button"
            style={{ ...styles.whatsAppQuoteBtn, height: 42, padding: '0 12px' }}
            onClick={handleWhatsAppQuote}
            title="Custom WhatsApp Quote"
          >
            <img src={WhatsappLogo} alt="whatsapp" style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </div>

      <ProductOptionsModal
        visible={isOptionsSheetVisible}
        onClose={() => setIsOptionsSheetVisible(false)}
        cardTitle={isOnlyQuote ? 'Add To Quote' : 'Add To Buy'}
        productData={variantsList}
        onAddOption={handleAddOption}
        bucketItemsMap={bucketItemsMap}
        customerId={customerId}
        hubId={hubId}
        purchaseType={isOnlyQuote ? 'RFQ' : 'BUY_NOW'}
      />

      <Bucket isOpen={isBucketOpen} onClose={() => setIsBucketOpen(false)} />

      {/* Brick Points Explanation Modal */}
      {isPointsModalOpen && (
        <div style={styles.statusModalOverlay} onClick={() => setIsPointsModalOpen(false)}>
          <div style={styles.pointsModalBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h4 style={{ margin: 0, fontSize: 16, color: '#2B2A29', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>🪙</span> Brick Points
              </h4>
              <button
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}
                onClick={() => setIsPointsModalOpen(false)}
                type="button"
              >
                ✕
              </button>
            </div>

            <div style={styles.pointsConversionBox}>
              <span style={{ fontSize: 24 }}>🪙</span>
              <div>
                <strong style={{ fontSize: 15, color: '#2B2A29', display: 'block' }}>1 Brick Point = {'1₹'} discount</strong>
                <span style={{ fontSize: 12, color: '#707B8C' }}>Use points for direct discounts on your future orders.</span>
              </div>
            </div>

            <div style={{ ...styles.pointsStatusBox, backgroundColor: customer?.customer_status === 3 ? '#DCFCE7' : '#FEF3C7', borderColor: customer?.customer_status === 3 ? '#86EFAC' : '#FDE68A' }}>
              {!customerId ? (
                <p style={styles.pointsStatusText}>Log in as a Professional Builder or Contractor to start earning points.</p>
              ) : customer?.customer_status === 3 ? (
                <p style={{ ...styles.pointsStatusText, color: '#166534' }}>✅ Your professional account is verified! You are eligible to earn and redeem Brick Points.</p>
              ) : (
                <p style={{ ...styles.pointsStatusText, color: '#92400E' }}>⚠️ <strong>Redemption Locked:</strong> Provide your GSTIN and get professional approval to unlock and redeem your points.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  pageShell: {
    backgroundColor: '#FAF9F6',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  loadingBox: {
    padding: 100,
    textAlign: 'center',
    color: '#707B8C',
    fontSize: 14,
  },
  contentStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
    padding: '24px 0 60px 0',
  },
  mainImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
    transition: 'filter 0.2s ease, opacity 0.2s ease',
  },
  toteFallbackWrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
    borderRadius: 12,
  },
  toteFallbackIcon: {
    width: 64,
    height: 64,
    opacity: 0.3,
  },
  bookmarkFloatBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 38,
    height: 38,
    borderRadius: 19,
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
  },
  thumbWrapper: {
    width: 68,
    height: 68,
    borderRadius: 10,
    backgroundColor: '#FFFFFF',
    border: '1.5px solid #E2E8F0',
    padding: 6,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'opacity 0.2s ease',
  },
  thumbImg: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  detailsColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 18,
  },
  zeptoOutOfStockBox: {
    backgroundColor: '#FEE2E2',
    border: '1px solid #FECACA',
    borderRadius: 12,
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: 3,
  },
  zeptoOutOfStockTitle: {
    fontSize: 13,
    fontWeight: '800',
    color: '#DC2626',
    letterSpacing: '-0.1px',
  },
  zeptoOutOfStockSub: {
    fontSize: 11.5,
    fontWeight: '500',
    color: '#EF4444',
  },
  outOfStockPillDesktop: {
    height: 44,
    minWidth: 140,
    backgroundColor: '#F1F5F9',
    border: '1.5px solid #CBD5E1',
    borderRadius: 10,
    color: '#64748B',
    fontSize: 12,
    fontWeight: '800',
    letterSpacing: '0.4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
  },
  outOfStockPillMobile: {
    height: 42,
    minWidth: 140,
    backgroundColor: '#F1F5F9',
    border: '1.5px solid #CBD5E1',
    borderRadius: 10,
    color: '#64748B',
    fontSize: 11.5,
    fontWeight: '800',
    letterSpacing: '0.4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    userSelect: 'none',
  },
  headerCluster: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  categoryBadge: {
    fontSize: 11.5,
    fontWeight: '800',
    color: '#C7061F',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  productTitleText: {
    margin: 0,
    fontSize: 24,
    fontWeight: '900',
    color: '#0F172A',
    lineHeight: '32px',
    letterSpacing: '-0.3px',
  },
  priceContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  priceRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    flexWrap: 'wrap',
  },
  priceWithUnitGroup: {
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: 4,
  },
  sellingPriceText: {
    fontSize: 26,
    fontWeight: '900',
    lineHeight: '30px',
  },
  unitSuffixText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
    userSelect: 'none',
  },
  mrpStrikeText: {
    fontSize: 15,
    color: '#94A3B8',
    textDecoration: 'line-through',
  },
  discountPill: {
    backgroundColor: '#FEF2F2',
    color: '#C7061F',
    border: '1px solid #FECDD3',
    padding: '2px 8px',
    borderRadius: 6,
    fontSize: 11.5,
    fontWeight: '800',
  },
  taxInclusiveText: {
    fontSize: 11.5,
    color: '#64748B',
    fontWeight: '500',
  },
  pointsStrip: {
    backgroundColor: '#FFFBEB',
    border: '1px solid #FDE68A',
    borderRadius: 10,
    padding: '10px 14px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
  },
  pointsStripLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  pointsStripText: {
    fontSize: 12.5,
    color: '#92400E',
  },
  pointsStripLink: {
    fontSize: 11.5,
    fontWeight: '800',
    color: '#D97706',
    textDecoration: 'underline',
  },
  zeptoAddBtn: {
    borderWidth: '1.5px',
    borderStyle: 'solid',
    borderRadius: 10,
    padding: '6px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(233,0,30,0.15)',
    minWidth: 124,
    height: 44,
    boxSizing: 'border-box',
  },
  zeptoAddBtnText: {
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: '0.4px',
  },
  optionsBadgeText: {
    fontSize: 9.5,
    fontWeight: '700',
    marginTop: 1,
  },
  qtyStepper: {
    width: 124,
    height: 44,
    backgroundColor: '#E9001E',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 4px',
    boxShadow: '0 2px 8px rgba(233, 0, 30, 0.25)',
    boxSizing: 'border-box',
  },
  stepperBtn: {
    width: 32,
    height: 32,
    background: 'transparent',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
  },
  stepperCount: {
    fontSize: 15,
    fontWeight: '900',
    color: '#FFFFFF',
    userSelect: 'none',
  },
  whatsAppQuoteBtn: {
    height: 44,
    padding: '0 16px',
    backgroundColor: '#FFFFFF',
    border: '1.5px solid #CBD5E1',
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
    fontWeight: '800',
    color: '#1E293B',
    cursor: 'pointer',
  },
  trustBadgesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: 10,
  },
  trustBadgeItem: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E8F0',
    borderRadius: 10,
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    fontWeight: '700',
    color: '#334155',
  },
  specsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #CBD5E1',
    padding: '18px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  specsTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: '800',
    color: '#0F172A',
  },
  specsTable: {
    display: 'flex',
    flexDirection: 'column',
  },
  specsTableRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 16,
    padding: '8px 0',
    borderBottom: '1px solid #F1F5F9',
    fontSize: 13,
  },
  specsLabel: {
    color: '#64748B',
    width: 150,
    minWidth: 150,
    flexShrink: 0,
    textAlign: 'left',
    fontWeight: '500',
  },
  specsValue: {
    fontWeight: '700',
    color: '#1E293B',
    textAlign: 'left',
    flex: 1,
  },
  noSpecsText: {
    fontSize: 13,
    color: '#64748B',
  },
  statusModalOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.55)',
    zIndex: 99999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    backdropFilter: 'blur(3px)',
  },
  pointsModalBox: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 22,
    width: '100%',
    maxWidth: 380,
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
  },
  pointsConversionBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    backgroundColor: '#F8FAFC',
    padding: 14,
    borderRadius: 10,
    marginBottom: 14,
    border: '1px solid #E2E8F0',
  },
  pointsStatusBox: {
    padding: 12,
    borderRadius: 10,
    border: '1px solid #FDE68A',
  },
  pointsStatusText: {
    margin: 0,
    fontSize: 12.5,
    lineHeight: '18px',
  },
};